<template>
  <div ref="wrapper" :class="className" :tabindex="-1" role="application">
    <div :class="ns.e('header')">
      <div :class="ns.e('title')">
        <slot name="title" :success="isSuccess">{{
          title ?? textInfo.doCaptcha
        }}</slot>
      </div>
      <span role="none" style="flex: auto" />
      <LtButton
        v-if="isRefresh"
        text
        :class="[ns.e('action'), ns.e('refresh')]"
        :disabled="actionLocked"
        @click="handleRefresh"
      >
        <slot name="refresh">
          <LtIcon><Refresh /></LtIcon>
        </slot>
      </LtButton>
    </div>

    <div
      v-loading="isLoading || slider?.isLoading"
      :class="[ns.e('image'), actionLocked && ns.em('image', 'locked')]"
      @click="handleImageClick"
    >
      <div v-if="image" :class="ns.e('image-inner')">
        <canvas
          ref="canvas"
          :class="ns.e('canvas')"
          :width="canvasSizeIn[0]"
          :height="canvasSizeIn[1]"
        />
        <div v-if="type === 'slide'" :class="ns.e('sub-image')">
          <canvas
            ref="subCanvas"
            :class="ns.e('sub-canvas')"
            :height="canvasSizeIn[1]"
            :style="subCanvasStyle"
          />
        </div>
      </div>

      <TransitionGroup
        v-if="type === 'point'"
        :name="`${defaultNamespace}-fade`"
        appear
      >
        <span
          v-for="(item, index) in pointers"
          :key="index"
          :class="ns.e('pointer')"
          :style="{ top: `${item[1]}%`, left: `${item[0]}%` }"
          @click="cancelPointer.bind(null, index)"
        >
          {{ index + 1 }}
        </span>
      </TransitionGroup>

      <Transition :name="`${defaultNamespace}-fade`">
        <div
          v-if="isSuccess || failed"
          :class="[
            ns.e('image-tip'),
            ns.em('image-tip', isSuccess ? 'success' : 'fail'),
          ]"
          @click="stopPropagation"
        >
          {{
            isSuccess
              ? successTip ?? textInfo.success
              : failTip ?? textInfo.fail
          }}
        </div>
      </Transition>
    </div>

    <CaptchaSlider
      v-if="type === 'slide'"
      ref="slider"
      :class="ns.em('slider', 'inner')"
      :target="currentTarget?.[0]"
      :tolerance="tolerance"
      :loading="isLoading"
      loading-lock
      :disabled="disabled || failLocked"
      :on-before-test="onBeforeTest"
      :on-success="handleSlideSuccess"
      :on-fail="handleSlideFail"
      :on-drag-start="handleDragStart"
      :on-drag="handleDrag"
      :on-drag-end="handleDragEnd"
    >
      <template #tip>
        <slot name="tip" :success="isSuccess">
          {{ tip ?? textInfo.slide }}
        </slot>
      </template>
    </CaptchaSlider>

    <div v-if="type === 'point'" :class="ns.e('text-list')">
      <div :class="ns.e('tip')">
        <slot name="tip" :success="isSuccess">
          {{ tip ?? textInfo.pointInOrder }}</slot
        >
      </div>
      <span>:</span>
      <slot name="texts" :texts="texts">
        <span
          v-for="(text, index) in texts || []"
          :key="index"
          :class="ns.e('text')"
        >
          {{ text }}
        </span>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { defaultNamespace, useNamespace } from '@lit-element/hooks'
import { Refresh } from '@inte-cloud/icons-vue'
import { parseTarget, textInfo } from './data'
import CaptchaSlider from './captcha-slider.vue'
import useCaptchaImg from './hooks/useCaptchaImg'

defineOptions({
  name: 'LtCaptchaPanel',
})

const props = defineProps({
  type: {
    type: String,
    default: 'slide',
  },
  slideTarget: [Number, Array],
  title: String,
  tip: String,
  successTip: String,
  failTip: String,
  image: [String, Function],
  tolerance: Number,
  canvasSize: Array,
  disabled: Boolean,
  loading: Boolean,
  onBeforeTest: Function,
  texts: Array,
  failLimit: {
    type: Number,
    default: 0,
  },
  remotePoint: Boolean,
  useTrigger: Boolean,
  visible: Boolean,
  hollowShape: [Function, String],
  hideDelay: {
    type: Number,
    default: 3000,
    validator: (value: number) => value >= 0,
  },
  isRefresh: Boolean,
})

const emits = defineEmits([
  'refresh',
  'drag',
  'drag-start',
  'drag-end',
  'fail',
  'success',
  'success-change',
])

const ns = useNamespace('captcha')

const slider = ref<any>()

const dragging = ref(false)
const success = ref(false)
const failed = ref(false)
const failedCount = ref(0)
const wrapper = ref<HTMLElement>()

const isSuccess = computed(() => !!slider.value?.isSuccess || success.value)
const currentTarget = computed(() => parseTarget(props.slideTarget))

const failLocked = computed(
  () => props.failLimit > 0 && failedCount.value >= props.failLimit
)

const canvasSizeIn: any = computed(() => {
  return [props.canvasSize?.[0] || 1000, props.canvasSize?.[1] || 600]
})

const {
  canvas,
  subCanvas,
  actionLocked,
  handleImageClick,
  subCanvasStyle,
  pointers,
  isLoading,
  cancelPointer,
  stopPropagation,
  updateImgPromise,
  drawImageNextFrame,
  getImgEle,
  imgReset,
  drawImage,
} = useCaptchaImg({
  slider,
  props,
  currentTarget,
  isSuccess,
  failFn: () => {
    ++failedCount.value
    success.value = false
    failed.value = true
    emits('fail')
  },
  successFn: (percent: any) => {
    success.value = true
    failed.value = false
    emits('success', percent)
  },
})

watch(
  [
    () => props.type,
    () => props.texts,
    () => props.texts?.length,
    () => props.remotePoint,
  ],
  () => {
    if (
      props.type === 'point' &&
      props.texts?.length &&
      !props.remotePoint &&
      getImgEle()
    ) {
      drawImageNextFrame()
    }
  }
)

watch([() => props.image, wrapper], async () => {
  await updateImgPromise(true)
  drawImageNextFrame()
})

watch(
  [
    currentTarget,
    () => props.canvasSize?.[0],
    () => props.canvasSize?.[1],
    () => props.hollowShape,
  ],
  () => {
    drawImageNextFrame()
  }
)

watch(
  () => props.visible,
  async (value) => {
    if (value) {
      await updateImgPromise()
      drawImageNextFrame()
    }
  }
)

watch(isSuccess, (value: boolean) => {
  emits('success-change', value)
})

function handleDragStart(percent: number) {
  dragging.value = true
  emits('drag-start', percent)
}

function handleDrag(percent: number) {
  emits('drag', percent)
}

function handleDragEnd(percent: number) {
  dragging.value = false
  emits('drag-end', percent)
}

function handleSlideSuccess(percent: number) {
  success.value = true
  failed.value = false
  emits('success', percent)
}

function handleSlideFail() {
  failed.value = true
  ++failedCount.value
  emits('fail')
}

const className = computed(() => {
  return [
    ns.b(),
    ns.m(props.type),
    {
      [ns.m('success')]: isSuccess.value,
      [ns.m('fail')]: !isSuccess.value && failed.value,
      [ns.m('dragging')]: dragging.value,
      [ns.m('disabled')]: props.disabled,
      [ns.m('loading')]: isLoading.value,
      [ns.m('fail-locked')]: failLocked.value,
    },
  ]
})

function handleRefresh() {
  if (!actionLocked.value) {
    emits('refresh')
  }
}

async function reset(newImage?: string | (() => Promise<string>)) {
  await imgReset(newImage)
  failedCount.value = 0
  success.value = false
  failed.value = false
  slider.value?.reset()
  // currentTarget.value = parseTarget()
}

onMounted(async () => {
  await updateImgPromise()
  drawImage()
})
defineExpose({
  reset,
})
</script>
